import { ChevronLeft, ChevronRight } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

import type { FileResult, FileTag } from "../types";

import { useFilePreviewUrl } from "../hooks/useFilePreviewUrl";

const SCROLL_STEP_PX = 200;
/** Small tolerance so floating-point scrollLeft rounding doesn't trap buttons disabled. */
const SCROLL_EDGE_TOLERANCE_PX = 1;

interface FileThumbnailStripProps {
  onSelectIndex: (index: number) => void;
  results: FileResult[];
  selectedIndex: number;
}

export function FileThumbnailStrip({
  onSelectIndex,
  results,
  selectedIndex,
}: FileThumbnailStripProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;

    if (!el) return;

    setCanScrollLeft(el.scrollLeft > SCROLL_EDGE_TOLERANCE_PX);
    setCanScrollRight(
      el.scrollLeft + el.clientWidth <
        el.scrollWidth - SCROLL_EDGE_TOLERANCE_PX,
    );
  }, []);

  useEffect(() => {
    updateScrollState();

    const el = scrollRef.current;

    if (!el) return;

    const observer = new ResizeObserver(updateScrollState);

    observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, [results.length, updateScrollState]);

  const scrollBy = (direction: -1 | 1) => {
    scrollRef.current?.scrollBy({
      behavior: "smooth",
      left: direction * SCROLL_STEP_PX,
    });
  };

  return (
    <div className="relative flex items-center">
      <ScrollButton
        direction="left"
        isVisible={canScrollLeft}
        onClick={() => scrollBy(-1)}
      />

      <div
        className="flex flex-1 gap-2 overflow-x-auto"
        onScroll={updateScrollState}
        ref={scrollRef}
        style={{ scrollbarWidth: "none" }}
      >
        {results.map((result, index) => (
          <ThumbnailItem
            index={index}
            isSelected={index === selectedIndex}
            key={result.id}
            onSelect={() => onSelectIndex(index)}
            result={result}
          />
        ))}
      </div>

      <ScrollButton
        direction="right"
        isVisible={canScrollRight}
        onClick={() => scrollBy(1)}
      />
    </div>
  );
}

interface ScrollButtonProps {
  direction: "left" | "right";
  isVisible: boolean;
  onClick: () => void;
}

function ScrollButton({ direction, isVisible, onClick }: ScrollButtonProps) {
  return (
    <button
      aria-hidden={!isVisible}
      aria-label={direction === "left" ? "Scroll left" : "Scroll right"}
      className={twMerge(
        "flex h-[80px] w-9 shrink-0 items-center justify-center rounded-xl border border-border-secondary bg-bg-primary text-text-tertiary transition-opacity hover:text-text-primary",
        direction === "left" ? "mr-2" : "ml-2",
        isVisible ? "opacity-100" : "pointer-events-none opacity-0",
      )}
      onClick={onClick}
      tabIndex={isVisible ? 0 : -1}
      type="button"
    >
      {direction === "left" ? (
        <ChevronLeft size={16} />
      ) : (
        <ChevronRight size={16} />
      )}
    </button>
  );
}

interface ThumbnailItemProps {
  index: number;
  isSelected: boolean;
  onSelect: () => void;
  result: FileResult;
}

function ThumbnailItem({ isSelected, onSelect, result }: ThumbnailItemProps) {
  const previewSrc = useFilePreviewUrl(result.file, result.previewUrl);
  const isLoading = result.status === "loading";
  const isTransferring = result.isTransferring ?? false;
  const showTransferOverlay = isLoading || isTransferring;

  const scoreLabel =
    result.status === "done" ? `Score: ${result.score}%` : "Score: --";

  return (
    <button
      className={twMerge(
        "flex min-w-[180px] items-center gap-2 px-3 py-2 text-left transition-colors rounded-xl bg-bg-primary",
        isSelected
          ? "border border-border-brand bg-bg-secondary"
          : "border border-[#EAECF0] hover:bg-[#F9FAFB]",
      )}
      onClick={onSelect}
      type="button"
    >
      <div className="relative h-12 w-12 shrink-0 overflow-hidden rounded-md border border-border-secondary bg-bg-secondary">
        {previewSrc && (
          <img
            alt={result.file.name}
            className="h-full w-full object-cover"
            src={previewSrc}
          />
        )}
        {showTransferOverlay && (
          <div className="absolute inset-0 flex items-center justify-center bg-bg-overlay/50">
            {result.uploadProgress === undefined ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-border-brand border-t-transparent" />
            ) : (
              <div className="flex flex-col items-center gap-1">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-border-brand border-t-transparent" />
                <span className="text-[10px] font-medium text-text-primary">
                  {result.uploadProgress}%
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex min-w-0 flex-col gap-1">
        <TagBadge isLoading={showTransferOverlay} tag={result.tag} />
        <span className="truncate text-xs font-medium text-text-primary">
          {result.displayName ?? result.file.name}
        </span>
        <span
          className={twMerge(
            "text-xs",
            scoreColorClass(result.status === "done" ? result.score : null),
          )}
        >
          {scoreLabel}
        </span>
      </div>
    </button>
  );
}

interface TagBadgeProps {
  isLoading: boolean;
  tag: FileTag;
}

function TagBadge({ isLoading, tag }: TagBadgeProps) {
  if (isLoading) {
    return (
      <span className="w-fit rounded-full bg-bg-secondary px-2 py-0.5 text-xs font-medium text-text-tertiary">
        Uploading...
      </span>
    );
  }

  const config = TAG_BADGE_CONFIG[tag];

  return (
    <span
      className={twMerge(
        "w-fit rounded-full border px-2 py-0.5 text-xs font-medium text-nowrap",
        config.classes,
      )}
    >
      {config.label}
    </span>
  );
}

const TAG_BADGE_CONFIG: Record<FileTag, { classes: string; label: string }> = {
  auto_checked: {
    classes:
      "border-border-success bg-bg-success-secondary text-text-success-primary",
    label: "Auto-checked",
  },
  failed: {
    classes:
      "border-border-error bg-bg-error-secondary text-text-error-primary",
    label: "Failed",
  },
  overridden: {
    classes: "border-border-secondary bg-bg-secondary text-text-secondary",
    label: "Overridden",
  },
  pending_review: {
    classes: "border-border-warning bg-[#FFFAEB] text-[#B54708]",
    label: "Pending review",
  },
};

function scoreColorClass(score: null | number): string {
  if (score === null) return "text-text-tertiary";
  if (score >= 85) return "text-text-success-primary";
  if (score >= 65) return "text-[#F79009]";

  return "text-text-error-primary";
}
